import { RangeTime, RangeTimeProps } from 'models/reusable-parts/range-time';
import {
  applySnapshot,
  getSnapshot,
  Instance,
  SnapshotIn,
  types,
} from 'mobx-state-tree';
import { LoadingState, LoadingStates } from 'globals/types';
import { TileTabDataProps } from 'ui-elements/tabs/FsTileTabs/FsTileTab/FsTileTab';
import {
  FsLineToggleChartSingleRowProps,
  GetMinMaxValue,
  getMinMaxValue,
} from 'ui-elements/charts/FsLineToggleChart/FsLineToggleChart.utils';
import {
  CashflowSingleChart,
  CashflowSingleChartProps,
} from './cashflow-single-chart';

export interface CashflowChartsProps {
  charts: CashflowSingleChartProps[];
  chartArguments: number[];
  density: string;
  rangeTime: RangeTimeProps;
  status: LoadingState;
}

export const CashflowCharts = types
  .model({
    charts: types.optional(types.array(CashflowSingleChart), []),
    chartArguments: types.optional(types.array(types.number), []),
    density: types.maybe(types.string),
    rangeTime: types.maybe(RangeTime),
    status: types.optional(
      types.enumeration(LoadingStates),
      LoadingState.INITIAL
    ),
  })
  .actions((self) => ({
    set(props: CashflowChartsProps): void {
      const currentSnapshot = getSnapshot(self);
      const newSnapshot = {
        ...currentSnapshot,
        ...props,
      };
      applySnapshot(self, newSnapshot);
    },
    updateStatus(status: LoadingState): void {
      const currentSnapshot = getSnapshot(self);
      const newSnapshot = {
        ...currentSnapshot,
        status,
      };
      applySnapshot(self, newSnapshot);
    },
  }))
  .views((self) => ({
    chartData(): FsLineToggleChartSingleRowProps[] {
      const { chartArguments, charts } = self;
      const chartValues = getSnapshot(charts).map(({ values }) => values);
      const chartInitialData = chartArguments.map((x, idx) => {
        const tabRowValues = chartValues
          .map((tabColumn, tabColumnIdx) => ({
            [`tab${tabColumnIdx}`]: chartValues[tabColumnIdx][idx],
          }))
          .reduce(
            (item, total) => ({
              ...total,
              ...item,
            }),
            {}
          );
        return {
          x,
          idx,
          ...tabRowValues,
          hidden: -1,
        };
      });
      const { min } = getMinMaxValue(chartInitialData);
      return chartInitialData.map((items) => ({
        ...items,
        hidden: min - 2,
      }));
    },
    tabs(): TileTabDataProps[] {
      return self.charts.map(({ label, price }, idx) => ({
        id: `${idx}`,
        label,
        value: price.displayValue(),
      }));
    },
  }))
  .views((self) => ({
    minMax(): GetMinMaxValue {
      return getMinMaxValue(self.chartData());
    },
  }));

export type CashflowChartsSnapshot = SnapshotIn<typeof CashflowCharts>;
export type CashflowChartsInstance = Instance<typeof CashflowCharts>;
